import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { Sparkles, Loader2, Copy, CalendarClock, Send, X, ExternalLink, Check, Trash2, ChevronDown, ChevronUp } from "lucide-react";
import { toast } from "sonner";
import Layout from "@/components/Layout";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useClientScope } from "@/hooks/useClientScope";
import { useAuth } from "@/context/AuthContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";

const TABS = [["ideas", "Post ideas"], ["projects", "PR projects"], ["planner", "Planner"], ["calendar", "Calendar"], ["voice", "Voice"]];
const btn = "inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-semibold transition-colors disabled:opacity-50";
const primary = `${btn} bg-indigo-600 hover:bg-indigo-700 text-indigo-50`;
const ghost = `${btn} border border-slate-200 text-slate-600 hover:text-slate-900 hover:bg-slate-50`;
const inputCls = "w-full px-3 py-2 rounded-lg border border-input bg-card text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-indigo-500/30";

const STATUS = {
  ai_draft: ["AI draft: needs approval", "bg-violet-500/10 text-violet-600 border-violet-500/20"],
  draft: ["Draft", "bg-slate-500/10 text-slate-500 border-slate-500/20"],
  scheduled: ["Scheduled", "bg-indigo-500/10 text-indigo-600 border-indigo-500/20"],
  publishing: ["Publishing", "bg-amber-500/10 text-amber-600 border-amber-500/20"],
  published: ["Published", "bg-emerald-500/10 text-emerald-600 border-emerald-500/20"],
  failed: ["Failed", "bg-red-500/10 text-red-600 border-red-500/20"],
  needs_manual: ["Post manually", "bg-amber-500/10 text-amber-600 border-amber-500/20"],
};

const PLATFORM_URL = { facebook: "https://www.facebook.com/", instagram: "https://www.instagram.com/" };

async function copyText(t) {
  try { await navigator.clipboard.writeText(t); toast.success("Caption copied"); }
  catch { toast.error("Could not copy. Select the text and copy it manually."); }
}

function toLocalInput(d) {
  const p = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}`;
}

function ScheduleDialog({ open, onClose, caption, ideaId, accounts, qs, onDone, defaultPlatform }) {
  const [text, setText] = useState("");
  const [acc, setAcc] = useState("");
  const [when, setWhen] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    setText(caption || "");
    const pref = accounts.find((a) => a.platform === defaultPlatform) || accounts[0];
    setAcc(pref?.id || "");
    const t = new Date(Date.now() + 3600e3);
    t.setMinutes(0, 0, 0);
    setWhen(toLocalInput(t));
  }, [open, caption, accounts, defaultPlatform]);

  const submit = async (mode) => {
    setBusy(true);
    try {
      const body = { account_id: acc, caption: text, idea_id: ideaId || null };
      if (mode === "schedule") body.due_at = new Date(when).toISOString();
      const { data } = await api.post(`/content/scheduled${qs}`, body);
      if (mode === "now") await api.post(`/content/scheduled/${data.id}/publish-now`);
      toast.success(mode === "now" ? "Sent" : mode === "schedule" ? "Scheduled" : "Saved as draft");
      onDone(); onClose();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="schedule-dialog">
        <DialogHeader>
          <DialogTitle>Schedule post</DialogTitle>
          <DialogDescription>Edit the caption, pick where and when. If auto-publishing isn't available for the account, you'll get a ready-to-post reminder instead.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <textarea value={text} onChange={(e) => setText(e.target.value)} rows={6} className={inputCls} data-testid="schedule-caption" />
          <div className="grid sm:grid-cols-2 gap-3">
            <label className="text-xs font-semibold text-slate-500">Account
              <select value={acc} onChange={(e) => setAcc(e.target.value)} className={`${inputCls} mt-1`} data-testid="schedule-account">
                {accounts.map((a) => <option key={a.id} value={a.id}>{a.name} ({a.platform})</option>)}
              </select>
            </label>
            <label className="text-xs font-semibold text-slate-500">When
              <input type="datetime-local" value={when} onChange={(e) => setWhen(e.target.value)} className={`${inputCls} mt-1`} data-testid="schedule-when" />
            </label>
          </div>
          {!accounts.length && <p className="text-sm text-amber-600">Connect an account first in <Link to="/accounts" className="underline">Connected Accounts</Link>.</p>}
        </div>
        <DialogFooter className="gap-2 sm:gap-2">
          <button className={ghost} disabled={busy || !acc || !text.trim()} onClick={() => submit("draft")}>Save draft</button>
          <button className={ghost} disabled={busy || !acc || !text.trim()} onClick={() => submit("now")} data-testid="schedule-now">Post now</button>
          <button className={primary} disabled={busy || !acc || !text.trim() || !when} onClick={() => submit("schedule")} data-testid="schedule-submit">Schedule</button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function IdeaCard({ idea, canWrite, onSchedule, onDismiss }) {
  const [text, setText] = useState(idea.caption_draft);
  return (
    <div className="bg-card rounded-2xl border border-slate-100 shadow-card p-5 space-y-3" data-testid="idea-card">
      <div className="flex items-start justify-between gap-3">
        <h3 className="font-heading text-lg text-slate-900">{idea.title}</h3>
        <div className="flex items-center gap-2 shrink-0 text-[11px] text-slate-400">
          <span className="capitalize">{idea.best_platform}</span><span>·</span><span>{idea.effort} effort</span>
        </div>
      </div>
      <p className="text-xs text-slate-500"><span className="font-semibold">Why:</span> {idea.why}</p>
      {idea.steps?.length > 0 && (
        <ol className="list-decimal pl-5 space-y-1 text-sm text-slate-600">{idea.steps.map((s, i) => <li key={i}>{s}</li>)}</ol>
      )}
      <textarea value={text} onChange={(e) => setText(e.target.value)} rows={4} className={inputCls} aria-label="Caption draft" />
      {idea.suggested_time && <p className="text-xs text-slate-400">Suggested time: {idea.suggested_time}</p>}
      <div className="flex flex-wrap gap-2">
        <button className={ghost} onClick={() => copyText(text)}><Copy className="w-3.5 h-3.5" />Copy</button>
        {canWrite && <button className={primary} onClick={() => onSchedule(idea, text)} data-testid="idea-schedule"><CalendarClock className="w-3.5 h-3.5" />Schedule / post</button>}
        {canWrite && <button className={`${ghost} ml-auto`} onClick={() => onDismiss(idea)} aria-label="Dismiss idea"><X className="w-3.5 h-3.5" /></button>}
      </div>
    </div>
  );
}

function Calendar({ items, canWrite, reload }) {
  const act = async (fn, okMsg) => {
    try { await fn(); if (okMsg) toast.success(okMsg); reload(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  if (!items.length) return <p className="text-sm text-slate-400 py-12 text-center">Nothing scheduled yet. Pick an idea and hit Schedule.</p>;
  return (
    <div className="space-y-3">
      {items.map((p) => {
        const [label, cls] = STATUS[p.status] || STATUS.draft;
        return (
          <div key={p.id} className="bg-card rounded-2xl border border-slate-100 shadow-card p-4 space-y-2" data-testid="scheduled-row">
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <div className="flex items-center gap-2 text-sm text-slate-600">
                <span className={`inline-flex px-2 py-0.5 rounded-full border text-xs font-medium ${cls}`}>{label}{p.simulated ? " (simulated)" : ""}</span>
                <span className="capitalize">{p.account_name || p.platform}</span>
                <span className="text-slate-400">{p.due_at ? new Date(p.due_at).toLocaleString([], { dateStyle: "medium", timeStyle: "short" }) : "No date"}</span>
              </div>
            </div>
            <p className="text-sm text-slate-700 whitespace-pre-wrap">{p.caption}</p>
            {p.error && <p className="text-xs text-amber-600">{p.error}</p>}
            {p.note && <p className="text-xs text-slate-400">{p.note}</p>}
            <div className="flex flex-wrap gap-2">
              <button className={ghost} onClick={() => copyText(p.caption)}><Copy className="w-3.5 h-3.5" />Copy</button>
              {canWrite && p.status === "ai_draft" && (
                <>
                  <button className={primary} data-testid="approve-draft" onClick={() => act(() => api.post(`/content/scheduled/${p.id}/approve`), "Approved as a draft. Schedule or post it from the Calendar.")}><Check className="w-3.5 h-3.5" />Approve</button>
                  <button className={ghost} data-testid="reject-draft" onClick={() => act(() => api.post(`/content/scheduled/${p.id}/reject`), "Draft removed")}><X className="w-3.5 h-3.5" />Reject</button>
                </>
              )}
              {p.status === "needs_manual" && (
                <a className={ghost} href={PLATFORM_URL[p.platform]} target="_blank" rel="noreferrer"><ExternalLink className="w-3.5 h-3.5" />Open {p.platform}</a>
              )}
              {canWrite && p.status === "needs_manual" && (
                <button className={primary} onClick={() => act(() => api.post(`/content/scheduled/${p.id}/mark-posted`), "Marked as posted")}><Check className="w-3.5 h-3.5" />I posted it</button>
              )}
              {canWrite && ["draft", "scheduled", "failed"].includes(p.status) && (
                <button className={ghost} onClick={() => act(() => api.post(`/content/scheduled/${p.id}/publish-now`), "Sent")}><Send className="w-3.5 h-3.5" />Post now</button>
              )}
              {canWrite && p.status !== "publishing" && p.status !== "ai_draft" && (
                <button className={`${ghost} ml-auto`} aria-label="Delete" onClick={() => act(() => api.delete(`/content/scheduled/${p.id}`))}><Trash2 className="w-3.5 h-3.5" /></button>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

const TRIGGERS = [["score_drop", "Score dropped"], ["damaged_post", "A post is going badly"], ["coordinated", "Coordinated comments"], ["threat", "Threats"]];

function Planner({ items, qs, canWrite, reload }) {
  const [busy, setBusy] = useState("");
  const [trigger, setTrigger] = useState("score_drop");
  const [playbook, setPlaybook] = useState(null);
  const drafts = items.filter((i) => i.status === "ai_draft");
  const run = async (key, path, body, done) => {
    setBusy(key);
    try { const { data } = await api.post(`/content/planner/${path}${qs}`, body); done(data); await reload(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(""); }
  };
  const msg = (what) => (d) => toast.success(d.source === "ai" ? `${what} written in their voice. Review before approving.` : `${what} ready from templates. Turn on AI in AI Settings for drafts in their voice.`);
  return (
    <div className="space-y-5">
      <p className="text-sm text-slate-500">Drafts only. Nothing here posts until a person approves it and then schedules or posts it from the Calendar.</p>
      {canWrite && (
        <div className="flex flex-wrap items-center gap-2">
          <button className={primary} disabled={!!busy} data-testid="plan-week" onClick={() => run("week", "week", {}, msg("A week of posts"))}>
            {busy === "week" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}Plan my week
          </button>
          <button className={ghost} disabled={!!busy} data-testid="plan-reply" onClick={() => run("reply", "reply", {}, msg("A reply"))}>
            {busy === "reply" && <Loader2 className="w-4 h-4 animate-spin" />}Reply to the latest post's issues
          </button>
          <span className="mx-1 text-slate-300">|</span>
          <select className="px-3 py-1.5 rounded-lg border border-input bg-card text-sm text-slate-700" value={trigger} onChange={(e) => setTrigger(e.target.value)} aria-label="Crisis trigger">
            {TRIGGERS.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
          </select>
          <button className={ghost} disabled={!!busy} data-testid="plan-crisis" onClick={() => run("crisis", "crisis", { trigger }, (d) => { setPlaybook(d.playbook); msg("A holding statement")(d); })}>
            {busy === "crisis" && <Loader2 className="w-4 h-4 animate-spin" />}Crisis playbook
          </button>
        </div>
      )}
      {playbook && (
        <div className="bg-card rounded-2xl border border-slate-100 shadow-card p-4 space-y-3 text-sm" data-testid="playbook">
          <p className="font-medium text-slate-900">Crisis playbook</p>
          {[["Do", playbook.do], ["Don't", playbook.dont], ["Escalate", playbook.escalate]].map(([h, list]) => (
            <div key={h}>
              <p className="text-xs uppercase tracking-wide text-slate-400">{h}</p>
              <ul className="list-disc pl-5 text-slate-700">{list.map((x) => <li key={x}>{x}</li>)}</ul>
            </div>
          ))}
          <p className="text-xs text-slate-400">The holding statement is in the drafts below.</p>
        </div>
      )}
      {drafts.length === 0
        ? <p className="text-sm text-slate-400 py-10 text-center">No AI drafts waiting. Plan a week or draft a reply.</p>
        : <Calendar items={drafts} canWrite={canWrite} reload={reload} />}
    </div>
  );
}

const LIST_FIELDS = [
  ["key_issues", "Key issues", "One per line, e.g. flood control"],
  ["tone_words", "Tone words", "e.g. warm, direct, humble"],
  ["languages", "Languages", "e.g. English, Bisaya"],
  ["signature_phrases", "Signature phrases", "Phrases or hashtags they keep using"],
  ["dos", "Always do", "Habits that make a post sound like them"],
  ["red_lines", "Never do (red lines)", "Topics, claims or attacks that are off limits"],
  ["sample_posts", "Past approved posts", "Paste 3-5 posts that sound exactly right, one per line"],
];

const DRAFT_LABELS = [
  ["tone_words", "Tone"], ["key_issues", "Key issues"], ["languages", "Languages"],
  ["signature_phrases", "Signature phrases"], ["dos", "Habits"], ["bio", "Background"],
];

function VoiceDraft({ draft, qs, canWrite, onChange }) {
  const have = DRAFT_LABELS.filter(([k]) => (Array.isArray(draft[k]) ? draft[k].length : draft[k]));
  const [sel, setSel] = useState(() => Object.fromEntries(have.map(([k]) => [k, true])));
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const chosen = have.map(([k]) => k).filter((k) => sel[k]);
  const run = async (fn, done) => {
    setBusy(true);
    try { await fn(); toast.success(done); onChange(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  return (
    <div className="rounded-xl border border-indigo-200 bg-indigo-50/40 p-4 space-y-3" data-testid="voice-draft">
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="text-sm font-semibold text-slate-800">Draft from their posts</p>
          <p className="text-xs text-slate-500 mt-0.5">
            {draft.source === "ai" ? "Written by AI" : "Pattern-based, no AI"} from {draft.based_on_posts} posts.
            Nothing is saved to the profile until you add it.
          </p>
        </div>
      </div>
      <ul className="space-y-2">
        {have.map(([k, label]) => (
          <li key={k} className="flex items-start gap-2">
            <input type="checkbox" className="mt-1" checked={!!sel[k]} disabled={!canWrite} data-testid={`draft-pick-${k}`}
              onChange={(e) => setSel({ ...sel, [k]: e.target.checked })} />
            <div className="min-w-0 text-sm">
              <span className="font-semibold text-slate-600">{label}: </span>
              <span className="text-slate-700">{Array.isArray(draft[k]) ? draft[k].join(", ") : draft[k]}</span>
              {k === "signature_phrases" && Object.keys(draft.evidence || {}).length > 0 && (
                <button type="button" onClick={() => setOpen(!open)} className="ml-2 inline-flex items-center gap-0.5 text-xs text-indigo-600">
                  evidence {open ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
                </button>
              )}
              {k === "signature_phrases" && open && (
                <ul className="mt-1 space-y-1">
                  {Object.entries(draft.evidence).flatMap(([ph, posts]) => posts.map((t, i) => (
                    <li key={`${ph}-${i}`} className="text-xs text-slate-500 border-l-2 border-slate-200 pl-2">{t}</li>
                  )))}
                </ul>
              )}
            </div>
          </li>
        ))}
      </ul>
      {canWrite && (
        <div className="flex gap-2">
          <button type="button" className={primary} disabled={busy || !chosen.length} data-testid="draft-apply"
            onClick={() => run(() => api.post(`/content/profile/draft/apply${qs}`, { fields: chosen }), "Added to the voice profile")}>
            Add selected
          </button>
          <button type="button" className={ghost} disabled={busy} data-testid="draft-dismiss"
            onClick={() => run(() => api.post(`/content/profile/draft/dismiss${qs}`), "Draft dismissed")}>
            Dismiss
          </button>
        </div>
      )}
    </div>
  );
}

function VoiceForm({ qs, canWrite }) {
  const [p, setP] = useState(null);
  const [saving, setSaving] = useState(false);
  const [learning, setLearning] = useState(false);
  const [rev, setRev] = useState(0);  // list boxes are uncontrolled; a new rev remounts them with fresh values
  const load = useCallback(() => (
    api.get(`/content/profile${qs}`).then(({ data }) => { setP(data); setRev((r) => r + 1); }).catch(() => setP(false))
  ), [qs]);
  useEffect(() => { load(); }, [load]);
  const learn = async () => {
    setLearning(true);
    try {
      const { data } = await api.post(`/content/profile/infer${qs}`);
      if (data.status === "too_few_posts") {
        toast.error(`Only ${data.posts} posts with text so far. COMO needs ${data.needed} to learn their voice.`);
      } else if (data.status === "already_running") {
        toast.message("Already learning from their posts");
      } else { toast.success("Draft ready to review"); await load(); }
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setLearning(false); }
  };
  if (p === null) return <div className="flex justify-center py-12"><Loader2 className="w-5 h-5 animate-spin text-indigo-600" /></div>;
  if (p === false) return <p className="text-sm text-slate-500">Could not load the voice profile.</p>;
  const set = (k, v) => setP({ ...p, [k]: v });
  const list = (k) => (p[k] || []).join("\n");
  const save = async () => {
    setSaving(true);
    try {
      const { exists, draft, ...body } = p;
      const { data } = await api.put(`/content/profile${qs}`, body);
      setP(data); toast.success("Voice profile saved");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };
  return (
    <div className="bg-card rounded-2xl border border-slate-100 shadow-card p-6 space-y-5" data-testid="voice-form">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm text-slate-500 max-w-xl">The more COMO knows about them, the more the ideas sound like them. This is used for every idea and caption draft, and the confirmed profile also guides comment analysis and keyword learning.</p>
        {canWrite && (
          <button type="button" className={ghost} onClick={learn} disabled={learning} data-testid="voice-learn">
            {learning ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />} Learn from their posts
          </button>
        )}
      </div>
      {p.draft && <VoiceDraft key={p.draft.generated_at} draft={p.draft} qs={qs} canWrite={canWrite} onChange={load} />}
      <div className="grid sm:grid-cols-2 gap-4">
        {[["politician_name", "Name"], ["office", "Office"], ["party", "Party"], ["region", "Region"]].map(([k, l]) => (
          <label key={k} className="text-xs font-semibold text-slate-500">{l}
            <input className={`${inputCls} mt-1`} value={p[k] || ""} disabled={!canWrite} onChange={(e) => set(k, e.target.value)} />
          </label>
        ))}
      </div>
      <label className="text-xs font-semibold text-slate-500 block">Background and bio
        <textarea className={`${inputCls} mt-1`} rows={3} value={p.bio || ""} disabled={!canWrite} onChange={(e) => set("bio", e.target.value)} />
      </label>
      <label className="text-xs font-semibold text-slate-500 block">Audience
        <input className={`${inputCls} mt-1`} value={p.audience || ""} disabled={!canWrite} onChange={(e) => set("audience", e.target.value)} />
      </label>
      <div className="grid sm:grid-cols-2 gap-4">
        {LIST_FIELDS.map(([k, l, ph]) => (
          <label key={k} className="text-xs font-semibold text-slate-500">{l}
            <textarea key={`${k}-${rev}`} className={`${inputCls} mt-1`} rows={4} placeholder={ph} disabled={!canWrite} defaultValue={list(k)}
              onBlur={(e) => set(k, e.target.value.split(/[\n,]/).map((x) => x.trim()).filter(Boolean))} />
          </label>
        ))}
      </div>
      {canWrite && <button className={primary} onClick={save} disabled={saving} data-testid="voice-save">{saving ? "Saving..." : "Save voice profile"}</button>}
    </div>
  );
}

export default function ContentStudio() {
  const { user } = useAuth();
  const canWrite = user?.role !== "client_viewer";
  const { ClientPicker, scopeParam, ready } = useClientScope();
  const qs = scopeParam ? `?${scopeParam}` : "";
  const [tab, setTab] = useState(() => { const t = new URLSearchParams(window.location.search).get("tab"); return TABS.some(([k]) => k === t) ? t : "ideas"; });
  const [ideas, setIdeas] = useState(null);
  const [items, setItems] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [generating, setGenerating] = useState(false);
  const [target, setTarget] = useState(null);

  const loadIdeas = useCallback(() => api.get(`/content/ideas${qs}`).then(({ data }) => setIdeas(data)).catch(() => setIdeas([])), [qs]);
  const loadItems = useCallback(() => api.get(`/content/scheduled${qs}`).then(({ data }) => setItems(data)).catch(() => {}), [qs]);

  useEffect(() => {
    if (!ready) return;
    setIdeas(null);
    loadIdeas(); loadItems();
    api.get(`/accounts${qs}`).then(({ data }) => setAccounts(data)).catch(() => {});
  }, [ready, qs, loadIdeas, loadItems]);

  useEffect(() => {
    if (tab !== "calendar" && tab !== "planner") return undefined;
    const t = setInterval(loadItems, 15000);
    return () => clearInterval(t);
  }, [tab, loadItems]);

  const generate = async () => {
    setGenerating(true);
    try {
      const { data } = await api.post(`/content/ideas/generate${qs}`);
      await loadIdeas();
      toast.success(data.source === "ai" ? "Fresh ideas in your voice" : "Ideas ready. Add an AI key in Integrations for ideas tuned to your voice.");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setGenerating(false); }
  };

  const dismiss = async (idea) => {
    try { await api.post(`/content/ideas/${idea.id}/dismiss${qs}`); loadIdeas(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const shown = (ideas || []).filter((i) => (tab === "projects" ? i.kind === "pr_project" : i.kind === "post"));
  const pending = items.filter((i) => i.status === "needs_manual").length;
  const waiting = items.filter((i) => i.status === "ai_draft").length;

  return (
    <Layout>
      <div className="animate-fade-in-up space-y-6">
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
          <div>
            <p className="text-sm text-slate-400 font-medium">Insight</p>
            <h1 className="font-heading text-3xl sm:text-4xl font-medium text-slate-900 tracking-tight">Content Studio</h1>
            <p className="text-sm text-slate-500 mt-1">Post ideas built from what landed well, what didn't, and what people are asking.</p>
          </div>
          <div className="flex items-center gap-3 flex-wrap">
            <ClientPicker />
            {canWrite && (tab === "ideas" || tab === "projects") && (
              <button className={primary} onClick={generate} disabled={generating} data-testid="generate-ideas">
                {generating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}Generate ideas
              </button>
            )}
          </div>
        </div>

        <div role="tablist" className="flex gap-1 border-b border-slate-100">
          {TABS.map(([k, l]) => (
            <button key={k} role="tab" aria-selected={tab === k} onClick={() => setTab(k)} data-testid={`tab-${k}`}
              className={`px-4 py-2 text-sm -mb-px border-b-2 transition-colors ${tab === k ? "border-indigo-600 text-slate-900 font-medium" : "border-transparent text-slate-500 hover:text-slate-900"}`}>
              {l}{k === "planner" && waiting > 0 && <span className="ml-1.5 text-[10px] px-1.5 py-0.5 rounded-full bg-violet-500/15 text-violet-600">{waiting}</span>}{k === "calendar" && pending > 0 && <span className="ml-1.5 text-[10px] px-1.5 py-0.5 rounded-full bg-amber-500/15 text-amber-600">{pending}</span>}
            </button>
          ))}
        </div>

        {(tab === "ideas" || tab === "projects") && (
          ideas === null ? <div className="flex justify-center py-16"><Loader2 className="w-5 h-5 animate-spin text-indigo-600" /></div>
            : shown.length === 0 ? (
              <div className="text-center py-16 space-y-3">
                <p className="text-sm text-slate-500">No {tab === "projects" ? "PR projects" : "post ideas"} yet.</p>
                {canWrite && <button className={primary} onClick={generate} disabled={generating}>Generate ideas</button>}
                <p className="text-xs text-slate-400">Tip: fill in the <button className="underline" onClick={() => setTab("voice")}>Voice</button> tab first for ideas that sound like them.</p>
              </div>
            ) : (
              <div className="grid lg:grid-cols-2 gap-4">
                {shown.map((i) => (
                  <IdeaCard key={i.id} idea={i} canWrite={canWrite} onDismiss={dismiss}
                    onSchedule={(idea, text) => setTarget({ idea, text })} />
                ))}
              </div>
            )
        )}
        {tab === "planner" && <Planner items={items} qs={qs} canWrite={canWrite} reload={loadItems} />}
        {tab === "calendar" && <Calendar items={items} canWrite={canWrite} reload={loadItems} />}
        {tab === "voice" && ready && <VoiceForm qs={qs} canWrite={canWrite} />}
      </div>

      <ScheduleDialog open={!!target} onClose={() => setTarget(null)} caption={target?.text} ideaId={target?.idea?.id}
        defaultPlatform={target?.idea?.best_platform} accounts={accounts} qs={qs}
        onDone={() => { loadItems(); loadIdeas(); setTab("calendar"); }} />
    </Layout>
  );
}
